import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, MapPin, Calendar, Package, Shield, ExternalLink, Mail, Phone, Globe, Filter, Grid, List } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { getSellerBySlug, getProductsBySeller, sellers } from '../data/products';

const SellerStorePage = () => {
  const { sellerSlug } = useParams();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('featured');
  
  const seller = getSellerBySlug(sellerSlug || '');
  const sellerProducts = getProductsBySeller(sellerSlug || '');

  // If seller not found, show default seller for demo
  const displaySeller = seller || sellers[0];
  const displayProducts = sellerProducts.length > 0 ? sellerProducts : [];

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'newest', label: 'Newest' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Seller Header */}
      <section className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Seller Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start space-x-6">
                <div className="bg-gradient-to-br from-orange-100 to-red-100 w-24 h-24 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Package className="h-12 w-12 text-orange-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">{displaySeller.name}</h1>
                    {displaySeller.verified && (
                      <div className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm">
                        <Shield className="h-4 w-4" />
                        <span>Verified</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-1">
                      <Star className="h-5 w-5 text-yellow-400 fill-current" />
                      <span className="font-medium">{displaySeller.rating}</span>
                      <span className="text-gray-600">({displaySeller.totalProducts} products)</span>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{displaySeller.location}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Est. {displaySeller.yearEstablished}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-4">{displaySeller.description}</p>
                  
                  {/* Specialties */}
                  <div className="flex flex-wrap gap-2">
                    {displaySeller.specialties.map((specialty, index) => (
                      <span key={index} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact & Actions */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Contact Seller</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center space-x-3 text-gray-600">
                  <Globe className="h-4 w-4" />
                  <span className="text-sm">{displaySeller.subdomain}</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span className="text-sm">contact@{displaySeller.slug}.com</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span className="text-sm">+880-1234-567890</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <button className="w-full bg-madeinbd-gradient text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>Send Message</span>
                </button>
                <button 
                  onClick={() => alert(`Visit: https://${displaySeller.subdomain}`)}
                  className="w-full border border-orange-600 text-orange-600 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Visit Store</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Products Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Products</h2>
              <p className="text-gray-600">{displayProducts.length} products available</p>
            </div>
            
            <div className="flex items-center space-x-4 mt-4 sm:mt-0">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          {displayProducts.length > 0 ? (
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                : 'grid-cols-1'
            }`}>
              {displayProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No products available</h3>
              <p className="text-gray-600">This seller hasn't listed any products yet.</p>
            </div>
          )}

          {/* Load More */}
          {displayProducts.length > 0 && displayProducts.length >= 12 && (
            <div className="text-center mt-12">
              <button className="bg-madeinbd-gradient text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity">
                Load More Products
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Seller Stats */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Seller Performance</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">{displaySeller.rating}</div>
              <div className="text-gray-600">Average Rating</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">{displaySeller.totalProducts}</div>
              <div className="text-gray-600">Total Products</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">98%</div>
              <div className="text-gray-600">Response Rate</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">24h</div>
              <div className="text-gray-600">Response Time</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SellerStorePage;